import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import {
	getCPEEnrolmentByPersonId,
	searchByPersonId,
	postUser,
	processCPEEnrolmentUpdate,
	getUserRolesByUserIdAndAppIdHistory,
	getUsersByOrgHierarchy,
	getChildrenByDomain,
	getAppRolesByAppId,
	postUserApp,
	postUserRole,
	postHierarchy,
	getActiveLeaveApplication,
	processLeaveRequest,
	getDelegateUserByUserId,
	getDelegateUserByForUserId,
	postDelegateUser,
	migrate,
	deleteCPEEnrolmentById,
	getTreeByDomain,
	getTreeById,
	getPayrollForms,
	getMigrationPendingWorkflows,
	getCPECapHistoryByPersonId,
	getMileageRates,
	postMileageRates,
} from "api";
import _ from "lodash";
import moment from "moment";
import { CME_APP_ID } from "../constants";
import { fetchBalanceByPersonId } from "../balance/balanceSlice";
import { CME_CONFIG } from "../constants";
import dayjs from "dayjs";

const isCacheValid = (timestamp) => {
	if (!timestamp) return false;
	const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
	return dayjs().diff(dayjs(timestamp)) < oneHour;
};

export const getFinancialYearStartDate = () => {
	const today = moment();
	let year;
	if (today.month() >= 7) year = today.year();
	else year = today.year() - 1;
	var date = new Date(year, 6, 1);
	return moment(date);
};

export const fetchMileageRates = createAsyncThunk(
	"admin/fetchMileageRates",
	async (forceRefresh, { getState, rejectWithValue }) => {
		try {
			const state = getState().cme.admin;
			const { mileageRates } = state;

			//return the same result for 1 hour, unless a refresh is forced
			//(e.g. right after an admin saves new rates)
			if (
				!forceRefresh &&
				mileageRates &&
				isCacheValid(mileageRates[0]?.lastFetch)
			) {
				return mileageRates;
			}

			const data = await getMileageRates();
			return data.map((d) => ({
				...d,
				fromDate: dayjs(d.fromDate).format("YYYY-MM-DD"),
				lastFetch: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
			}));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postMileageRatesThunk = createAsyncThunk(
	"admin/postMileageRates",
	async (data, { rejectWithValue }) => {
		try {
			await postMileageRates(data);
			return true;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchMigrationPendingWorkflows = createAsyncThunk(
	"admin/fetchMigrationPendingWorkflows",
	async ({ userId, organisationHierarchyId }, { rejectWithValue }) => {
		try {
			const data = await getMigrationPendingWorkflows({
				userId,
				organisationHierarchyId,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchPayrollForms = createAsyncThunk(
	"admin/fetchPayrollForms",
	async (personId, { rejectWithValue }) => {
		try {
			const data = await getPayrollForms(personId);
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const validateCpeEnrolment = createAsyncThunk(
	"admin/validateCpeEnrolment",
	async (cpe, { getState, dispatch }) => {
		//note entitlement amount is not validated as they have default values
		const { fte, specialPlanYears, startDate, endDate } = cpe;

		let errors = [];

		if (!fte || !specialPlanYears || !startDate)
			errors.push(
				"Please fill out all mandatory fields marked with a red asterisk"
			);
		const state = getState();
		const pcpe = selectAdminCpeEnrolment(state);

		const maxStartDate = _.orderBy(
			pcpe,
			["startDate"],
			["desc"]
		)?.startDate;

		if (startDate <= maxStartDate)
			errors.push(
				"You can't make a change that is dated prior to an existing CPE enrolment date. Please use a date greater than the current active record"
			);

		if (endDate <= startDate)
			errors.push(
				"End date cannot be on or before the start date. Please use a date greater than the start date"
			);

		if (startDate <= moment("2020-11-30").toISOString())
			errors.push(
				"Waitemata's CME balances prior to Dec-20 have been imported in. Please choose a start date of Dec-20 or greater"
			);

		dispatch(validationErrorsCpeEdited(errors));
		return errors.length;
	}
);

export const validatePerson = createAsyncThunk(
	"admin/validatePerson",
	async ({ person, organisation }, { dispatch }) => {
		const config = CME_CONFIG[organisation];
		const {
			userId,
			firstName,
			lastName,
			employeeId,
			jobTitle,
			email,
			payrollGroup,
		} = person;
		let errors = [];
		if (
			(!userId && config.Admin.RequireUserId) ||
			!firstName ||
			!lastName ||
			!employeeId ||
			!jobTitle ||
			!email ||
			(!payrollGroup && config.Admin.EnablePayrollGroup)
		)
			errors.push(
				"Please fill out all mandatory fields marked with a red asterisk"
			);
		dispatch(validationErrorsEdited(errors));
		return errors.length;
	}
);

export const postTeam = createAsyncThunk(
	"admin/postTeam",
	async (data, { rejectWithValue }) => {
		try {
			await postHierarchy(data);
			return true;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchMigrate = createAsyncThunk(
	"admin/migrate",
	async (
		{ userId, migrateUserId, organisationHierarchyId },
		{ rejectWithValue }
	) => {
		try {
			await migrate({ userId, migrateUserId, organisationHierarchyId });
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchCapHistory = createAsyncThunk(
	"admin/fetchCapHistory",
	async (personId, { rejectWithValue }) => {
		try {
			const data = await getCPECapHistoryByPersonId({ personId });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchCpeEnrolment = createAsyncThunk(
	"admin/fetchCpeEnrolment",
	async (personId, { rejectWithValue }) => {
		try {
			const data = await getCPEEnrolmentByPersonId({ personId });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchPersonById = createAsyncThunk(
	"admin/fetchPersonById",
	async (personId, { rejectWithValue }) => {
		try {
			const data = await searchByPersonId({ personId });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchUserRoles = createAsyncThunk(
	"admin/fetchUserRoles",
	async ({ userId }, { rejectWithValue }) => {
		try {
			const data = await getUserRolesByUserIdAndAppIdHistory({
				userId,
				appId: CME_APP_ID,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTeamUserRoles = createAsyncThunk(
	"admin/fetchTeamUserRoles",
	async ({ organisationHierarchyId, domain }, { rejectWithValue }) => {
		try {
			const response = await getUsersByOrgHierarchy({
				organisationHierarchyId,
				domain,
				deepSearch: false,
				includeNewUser: false,
				appId: CME_APP_ID,
			});
			return response;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postPerson = createAsyncThunk(
	"admin/postPerson",
	async (data, { rejectWithValue }) => {
		try {
			const response = await postUser(data);
			await postUserApp({ userId: data.id, appId: CME_APP_ID });
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postCpe = createAsyncThunk(
	"admin/postCpe",
	async ({ nzd, leave }, { rejectWithValue, dispatch }) => {
		try {
			await processCPEEnrolmentUpdate(nzd);
			const response = await processCPEEnrolmentUpdate(leave);
			dispatch(fetchBalanceByPersonId(nzd.personId));
			dispatch(fetchCpeEnrolment(nzd.personId));
			dispatch(fetchCapHistory(nzd.personId));
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postDeleteCpeById = createAsyncThunk(
	"admin/postDeleteCpeById",
	async ({ dollarsId, leaveId, personId }, { rejectWithValue, dispatch }) => {
		try {
			if (dollarsId) await deleteCPEEnrolmentById(dollarsId);
			if (leaveId) await deleteCPEEnrolmentById(leaveId);
			dispatch(fetchBalanceByPersonId(personId));
			dispatch(fetchCpeEnrolment(personId));
			dispatch(fetchCapHistory(personId));
			return "success";
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTeams = createAsyncThunk(
	"admin/fetchTeams",
	async (domain, { rejectWithValue }) => {
		try {
			const data = await getChildrenByDomain({ domain });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTreeById = createAsyncThunk(
	"admin/fetchTreeById",
	async (id, { rejectWithValue }) => {
		try {
			const data = await getTreeById({
				id,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTree = createAsyncThunk(
	"admin/fetchTree",
	async (
		{ domain, allowAllSelect = true, hideChildren = false },
		{ rejectWithValue }
	) => {
		try {
			const data = await getTreeByDomain({
				domain,
				allowAllSelect,
				hideChildren,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTreeParentsOnly = createAsyncThunk(
	"admin/fetchTreeParentsOnly",
	async ({ domain }, { rejectWithValue }) => {
		try {
			const data = await getTreeByDomain({
				domain,
				allowAllSelect: true,
				hideChildren: true,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchRoles = createAsyncThunk(
	"admin/fetchRoles",
	async (_, { rejectWithValue }) => {
		try {
			const data = await getAppRolesByAppId({ appId: CME_APP_ID });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postTeamMembership = createAsyncThunk(
	"admin/postTeamMembership",
	async (
		{
			id,
			organisationHierarchyId,
			userId,
			roleId,
			priority,
			isDefault,
			startDate,
			endDate,
			isDeleted,
			domain,
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			await postUserApp({ userId, appId: CME_APP_ID });
			await postUserRole({
				id,
				organisationHierarchyId,
				userId,
				roleId,
				priority,
				isDefault,
				startDate,
				endDate,
				isDeleted,
			});
			await dispatch(fetchUserRoles({ userId }));
			await dispatch(
				fetchTeamUserRoles({ organisationHierarchyId, domain })
			);
			return;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchLeaveApplications = createAsyncThunk(
	"admin/fetchLeaveApplications",
	async ({ teamId, periodEnding }, { rejectWithValue }) => {
		try {
			const data = await getActiveLeaveApplication({
				minLeaveDate: periodEnding,
				teamId,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postProcessLeaveRequest = createAsyncThunk(
	"admin/postProcessLeaveRequest",
	async ({ formId, statusId }, { rejectWithValue }) => {
		try {
			return await processLeaveRequest({ formId, statusId });
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchUserDelegates = createAsyncThunk(
	"admin/fetchUserDelegates",
	async ({ userId }, { rejectWithValue }) => {
		try {
			const myDelegateUsers = await getDelegateUserByUserId({
				userId,
			});
			const usersDelegatedForMe = await getDelegateUserByForUserId({
				forUserId: userId,
			});
			return [...myDelegateUsers, ...usersDelegatedForMe];
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postUserDelegate = createAsyncThunk(
	"admin/postUserDelegate",
	async (
		{
			thisPersonId,
			id,
			userId,
			delegatedForUser,
			type,
			startDate,
			endDate,
			ccEmail,
			isInactive = false,
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			await postDelegateUser([
				{
					id,
					userId,
					delegatedForUser,
					type,
					startDate,
					endDate,
					ccEmail,
					isInactive,
				},
			]);
			await dispatch(fetchUserDelegates({ userId: thisPersonId }));
			return;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const initialState = {
	person: {},
	cpeEnrolment: [],
	userRoles: [],
	userDelegates: [],
	teamUserRoles: [],
	roles: [],
	teams: [],
	leaveApplications: [],
	status: "idle",
	error: null,
	validationErrors: [],
	validationErrorsCpe: [],
	tree: null,
	treeParentsOnly: null,
	editTree: {},
	payrollForms: [],
	migrationPendingWorkflows: [],
	capHistory: [],
	mileageRates: [],
};

const adminSlice = createSlice({
	name: "admin",
	initialState,
	reducers: {
		validationErrorsEdited(state, action) {
			state.validationErrors = action.payload;
		},
		errorEdited(state, action) {
			state.error = action.payload;
		},
		validationErrorsCpeEdited(state, action) {
			state.validationErrorsCpe = action.payload;
		},
		personEdited(state, action) {
			state.person = action.payload;
		},
		cpeEnrolmentReceived(state, action) {
			state.cpeEnrolment = action.payload;
		},
		userRolesReceived(state, action) {
			state.userRoles = action.payload;
		},
		teamEdited(state, action) {
			state.team = action.payload;
		},
		teamUserRolesReceived(state, action) {
			state.teamUserRoles = action.payload;
		},
		statusEdited(state, action) {
			state.status = action.payload;
		},
	},
	extraReducers: {
		RESET_ADMIN: (state, _) => {
			state.person = {};
			state.cpeEnrolment = [];
			state.userRoles = [];
			state.teamUserRoles = [];
			state.leaveApplications = [];
			state.error = null;
			state.validationErrors = [];
			state.validationErrorsCpe = [];
			state.editTree = {};
			state.payrollForms = [];
			state.migrationPendingWorkflows = [];
			state.capHistory = [];
		},
		[postPerson.pending]: (state, action) => {
			state.status = "loading";
		},
		[postPerson.fulfilled]: (state, action) => {
			state.status = "success";
			state.person = action.payload;
		},
		[postPerson.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postCpe.pending]: (state, action) => {
			state.status = "loading";
		},
		[postCpe.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postCpe.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postDeleteCpeById.pending]: (state, action) => {
			state.status = "loading";
		},
		[postDeleteCpeById.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postDeleteCpeById.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postTeam.pending]: (state, action) => {
			state.status = "loading";
		},
		[postTeam.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postTeam.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postTeamMembership.pending]: (state, action) => {
			state.status = "loading";
		},
		[postTeamMembership.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postTeamMembership.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchPersonById.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchPersonById.fulfilled]: (state, action) => {
			state.status = "idle";
			state.person = action.payload; //set person data
		},
		[fetchPersonById.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.person = {};
		},
		[fetchTeams.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTeams.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedTeams = _.orderBy(action.payload, ["name"], ["asc"]);
			state.teams = sortedTeams;
		},
		[fetchTeams.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.teams = [];
		},
		[fetchRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedRoles = _.orderBy(
				action.payload,
				["roleName"],
				["asc"]
			);
			state.roles = sortedRoles;
		},
		[fetchRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.roles = [];
		},
		[fetchUserRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchUserRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedUserRoles = _.orderBy(
				action.payload,
				["organisationHierarchyName", "roleName"],
				["asc", "asc"]
			);
			state.userRoles = sortedUserRoles;
		},
		[fetchUserRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.userRoles = {};
		},
		[fetchTeamUserRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTeamUserRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sorted = _.orderBy(
				action.payload,
				["fullName", "roleName"],
				["asc", "asc"]
			);
			state.teamUserRoles = sorted;
		},
		[fetchTeamUserRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.teamUserRoles = {};
		},
		[fetchCapHistory.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchCapHistory.fulfilled]: (state, action) => {
			state.status = "idle";
			state.capHistory = action.payload; //set cpe enrolment data
		},
		[fetchCapHistory.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.capHistory = [];
		},
		[fetchCpeEnrolment.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchCpeEnrolment.fulfilled]: (state, action) => {
			state.status = "idle";
			state.cpeEnrolment = action.payload; //set cpe enrolment data
		},
		[fetchCpeEnrolment.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.cpeEnrolment = [];
		},
		[fetchLeaveApplications.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchLeaveApplications.fulfilled]: (state, action) => {
			state.status = "idle";
			state.leaveApplications = action.payload;
		},
		[fetchLeaveApplications.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.leaveApplications = [];
		},
		[postProcessLeaveRequest.pending]: (state, action) => {
			state.status = "loading";
		},
		[postProcessLeaveRequest.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postProcessLeaveRequest.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchUserDelegates.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchUserDelegates.fulfilled]: (state, action) => {
			state.status = "idle";
			state.userDelegates = action.payload;
		},
		[fetchUserDelegates.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.userDelegates = [];
		},
		[fetchMigrate.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchMigrate.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[fetchMigrate.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchTree.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTree.fulfilled]: (state, action) => {
			state.status = "idle";
			state.tree = action.payload;
		},
		[fetchTree.rejected]: (state, action) => {
			state.status = "failed";
			state.tree = null;
		},
		[fetchTreeById.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTreeById.fulfilled]: (state, action) => {
			state.status = "idle";
			state.editTree = action.payload;
		},
		[fetchTreeById.rejected]: (state, action) => {
			state.status = "failed";
			state.editTree = {};
		},
		[fetchTreeParentsOnly.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTreeParentsOnly.fulfilled]: (state, action) => {
			state.status = "idle";
			state.treeParentsOnly = action.payload;
		},
		[fetchTreeParentsOnly.rejected]: (state, action) => {
			state.status = "failed";
			state.treeParentsOnly = null;
		},
		[fetchPayrollForms.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchPayrollForms.fulfilled]: (state, action) => {
			state.status = "idle";
			state.payrollForms = action.payload;
		},
		[fetchPayrollForms.rejected]: (state, action) => {
			state.status = "failed";
			state.payrollForms = null;
		},
		[fetchMigrationPendingWorkflows.fulfilled]: (state, action) => {
			state.migrationPendingWorkflows = action.payload;
		},
		[fetchMileageRates.fulfilled]: (state, action) => {
			state.mileageRates = action.payload;
		},
	},
});

export const {
	personEdited,
	cpeEnrolmentReceived,
	userRolesReceived,
	teamUserRolesReceived,
	validationErrorsEdited,
	errorEdited,
	validationErrorsCpeEdited,
	statusEdited,
} = adminSlice.actions;

export const selectAdminPerson = (state) => state.cme.admin.person;
export const selectAdminCpeEnrolment = (state) => state.cme.admin.cpeEnrolment;
export const selectAdminUserRoles = (state) => state.cme.admin.userRoles;
export const selectAdminUserDelegates = (state) =>
	state.cme.admin.userDelegates;
export const selectAdminTeamUserRoles = (state) =>
	state.cme.admin.teamUserRoles;
export const selectAdminTeams = (state) => state.cme.admin.teams;
export const selectAdminRoles = (state) => state.cme.admin.roles;
export const selectAdminLeaveApplications = (state) =>
	state.cme.admin.leaveApplications;

export const selectAdminStatus = (state) => state.cme.admin.status;
export const selectAdminValidationErrors = (state) =>
	state.cme.admin.validationErrors;
export const selectAdminValidationErrorsCpe = (state) =>
	state.cme.admin.validationErrorsCpe;
export const selectAdminError = (state) => state.cme.admin.error;

export const selectAdminCapHistory = (state) => state.cme.admin.capHistory;

export const selectAdminCpeData = createSelector(
	[selectAdminCpeEnrolment],
	(data) => {
		const today = moment();
		//distinct rows of start dates so we can render a table of CPE history
		const startDates =
			data && data.length > 0
				? [
						...new Set(
							data
								.map((d) => d.startDate)
								.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0))
						),
				  ]
				: [];

		const rows =
			startDates && startDates.length > 0
				? startDates.map((startDate) => {
						const dollars = data.find(
							(d) => d.startDate === startDate && d.unit === "NZD"
						);
						const leave = data.find(
							(d) =>
								d.startDate === startDate &&
								["Days", "DAYS", "Hours"].includes(d.unit)
						);
						return {
							leaveUnit: leave?.unit,
							personId: dollars?.personId ?? leave?.personId,
							type: dollars?.type ?? leave?.type,
							isManualEnrolment:
								dollars?.isManualEnrolment ??
								leave?.isManualEnrolment,
							isAlternateEmploy:
								dollars?.isAlternateEmploy ??
								leave?.isAlternateEmploy,
							specialPlanYears:
								dollars?.specialPlanYears ??
								leave?.specialPlanYears,
							fte: dollars?.qualifyingFTE ?? leave?.qualifyingFTE,
							startDate: dollars?.startDate ?? leave?.startDate,
							endDate: dollars?.endDate ?? leave?.endDate,
							entitlementAmount: dollars?.entitlementAmount,
							entitlementAmountLeave: leave?.entitlementAmount,
							calculatedAmount:
								dollars?.calculatedEntitlementAmount,
							calculatedAmountLeave:
								leave?.calculatedEntitlementAmount,
							dollarsId: dollars?.personCPEEnrolmentId,
							leaveId: leave?.personCPEEnrolmentId,
							isTermination:
								dollars?.isTermination ?? leave?.isTermination,
							isActive:
								today >=
									moment(
										dollars?.startDate ?? leave?.startDate,
										"YYYY-MM-DD"
									) &&
								today <=
									moment(
										dollars?.endDate ?? leave?.endDate,
										"YYYY-MM-DD"
									) &&
								!(
									dollars?.isTermination ??
									leave?.isTermination
								),
							proratedFte:
								//CPD is always 1, no matter what
								(dollars?.type ?? leave?.type) === "CPD"
									? 1
									: dollars?.isAlternateEmploy ??
									  leave?.isAlternateEmploy
									? (dollars?.qualifyingFTE ??
											leave?.qualifyingFTE) > 1
										? 1
										: dollars?.qualifyingFTE ??
										  leave?.qualifyingFTE
									: 1,
						};
				  })
				: [];

		return rows;
	}
);

export const selectAdminActiveCpeData = createSelector(
	[selectAdminCpeData],
	(data) => {
		return data && data.length > 0
			? data.find((r) => r.isActive) ?? data[0]
			: null;
	}
);

export const selectAdminTree = (state) => state.cme.admin.tree;
export const selectAdminTreeParentsOnly = (state) =>
	state.cme.admin.treeParentsOnly;
export const selectAdminEditTree = (state) => state.cme.admin.editTree;

export const selectPayrollForms = (state) => state.cme.admin.payrollForms;

export const selectMigrationPendingWorkflows = (state) =>
	state.cme.admin.migrationPendingWorkflows;

export const selectMileageRates = (state) => state.cme.admin.mileageRates;

export default adminSlice.reducer;
